"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Button from "../../ui/Button";
import BellIcon from "@/components/icons/BellIcon";
import RouteIcon from "@/components/icons/RouteIcon";
import DeliveryTruckIcon from "@/components/icons/DeliveryTruckIcon";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon";

type StepKey = "subscribe" | "link" | "eta";

const STEP_IMAGES: Record<StepKey, { desktop: string; mobile: string }> = {
  subscribe: {
    desktop: "/media/pages/sameday-delivery/desktop/step1.webp",
    mobile: "/media/pages/sameday-delivery/mobile/step1.webp",
  },
  link: {
    desktop: "/media/pages/sameday-delivery/desktop/step2.webp",
    mobile: "/media/pages/sameday-delivery/mobile/step2.webp",
  },
  eta: {
    desktop: "/media/pages/sameday-delivery/desktop/step3.webp",
    mobile: "/media/pages/sameday-delivery/mobile/step3.webp",
  },
};

const ITEMS: {
  key: StepKey;
  step: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  cta?: { label: string; href: string };
}[] = [
    {
      key: "subscribe",
      step: 1,
      title: "Text to Subscribe",
      description:
        "Simply text the number 2 to 66358 to opt-in. This activates our notification system so you can receive instant updates about your order status.",
      icon: <BellIcon size={18} />,
      cta: { label: "Send text now", href: "sms:66358?body=2" },
    },
    {
      key: "link",
      step: 2,
      title: "Tracking Link",
      description:
        "After checkout you'll receive a secure tracking link by SMS and email when your driver is assigned, open it anytime for live status.",
      icon: <RouteIcon size={18} />,
    },
    {
      key: "eta",
      step: 3,
      title: "Real-time ETA updates",
      description:
        "Watch your order move from packed to out-for-delivery. ETAs show as a live window that updates with traffic and routing, if we run late, you will see the new time plus options to wait, reschedule, or reach support.",
      icon: <DeliveryTruckIcon size={18} />,
    },
  ];

export default function LiveOrderTrackingSection() {
  const [open, setOpen] = useState<StepKey>("subscribe");

  return (
    <section className="w-full max-w-none" aria-labelledby="live-tracking-heading">
      <div className="w-full">
        <h2
          id="live-tracking-heading"
          className="text-h3-mobile font-bold text-text-primary sm:text-h3-desktop"
        >
          Live order tracking
        </h2>

        <div className="mt-6 rounded-2xl bg-blue-5 p-4 sm:p-6">
          <div className="grid gap-6 lg:grid-cols-2 lg:items-stretch lg:gap-8">

            {/* Accordion, below image on mobile, left on desktop */}
            <div className="order-2 flex flex-col lg:order-1">
              <div className="flex flex-col gap-2">
                {ITEMS.map((item) => {
                  const expanded = open === item.key;
                  return (
                    <Button
                      raw
                      key={item.key}
                      type="button"
                      onClick={() => setOpen(item.key)}
                      aria-expanded={expanded}
                      className={[
                        "w-full rounded-xl text-left transition-all duration-200",
                        expanded
                          ? "bg-constant-white p-4 shadow-sm ring-1 ring-blue-10"
                          : "bg-constant-white/50 px-4 py-3",
                      ].join(" ")}
                    >
                      {/* Row header, always visible */}
                      <div className="flex items-center gap-3">
                        {/* Step number badge */}
                        <span
                          className={[
                            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums transition-colors",
                            expanded
                              ? "bg-blue-80 text-constant-white"
                              : "bg-blue-10 text-blue-80",
                          ].join(" ")}
                          aria-hidden
                        >
                          {item.step}
                        </span>

                        {/* Icon */}
                        <span
                          className={[
                            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors",
                            expanded ? "bg-blue-10 text-blue-80" : "bg-blue-5 text-blue-60",
                          ].join(" ")}
                        >
                          {item.icon}
                        </span>

                        {/* Title */}
                        <span
                          className={[
                            "flex-1 text-body-md font-semibold leading-snug transition-colors",
                            expanded ? "text-text-primary" : "text-text-secondary",
                          ].join(" ")}
                        >
                          {item.title}
                        </span>

                        {/* Expand indicator */}
                        <span
                          className={[
                            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-all duration-200",
                            expanded
                              ? "border-blue-15 bg-blue-10 text-blue-80"
                              : "border-neutral-10 bg-constant-white text-neutral-40",
                          ].join(" ")}
                          aria-hidden
                        >
                          <ChevronRightIcon
                            size={13}
                            className={`transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
                          />
                        </span>
                      </div>

                      {/* Expanded body */}
                      {expanded && (
                        <div className="mt-3 pl-[4.25rem] max-sm:pl-0">
                          <p className="text-body-sm leading-relaxed text-text-secondary sm:text-body-md">
                            {item.description}
                          </p>

                          {item.cta && (
                            <Button asChild variant="primary" size="md" className="mt-4 rounded-xl px-5 py-2.5">
                              <Link href={item.cta.href} onClick={(e) => e.stopPropagation()}>
                                <span className="flex items-center gap-2 w-max">
                                  <span>{item.cta.label}</span>
                                  <ChevronRightIcon size={14} color="white" />
                                </span>
                              </Link>
                            </Button>
                          )}
                        </div>
                      )}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Preview image, first on mobile, right on desktop */}
            <div className="order-1 lg:order-2">
              <div className="overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white p-0 sm:p-3 lg:p-4">
                <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-neutral-5 lg:aspect-[16/9]">
                  <Image
                    src={STEP_IMAGES[open].desktop}
                    alt={`Step ${ITEMS.find((i) => i.key === open)?.step}, ${ITEMS.find((i) => i.key === open)?.title}`}
                    fill
                    className="object-cover object-center max-lg:hidden"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    quality={100}
                    unoptimized
                  />
                  <Image
                    src={STEP_IMAGES[open].mobile}
                    alt={`Step ${ITEMS.find((i) => i.key === open)?.step}, ${ITEMS.find((i) => i.key === open)?.title}`}
                    fill
                    className="object-cover object-center lg:hidden"
                    sizes="100vw"
                    quality={100}
                    unoptimized
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
